// The owner's refused revoke of a till (UC-61): the server answers 409
// CASH_POINT_IN_USE while other active tills send their cash to it, with
// their names in its details. The page keeps the names, not a sentence, so
// the words follow the language on screen.
//
// And what each till last reported (UC-13, #978): its heartbeat's last seen
// time, online mark, app build, waiting sales and platform, as D7 shows them.

import type { components } from '../api/types';
import { formatWhen } from '../format/time';
import { countForm } from '../help/shelf';
import { fill, isArabic, message, type Language } from '../i18n/messages';

export interface Refusal {
	/** The tills that send their cash to the refused one, as the server named them. */
	names: string[];
	/** The server's own words, for an answer that named none. */
	text: string;
}

interface Answer {
	code?: string;
	message?: string;
	details?: Record<string, unknown>;
}

/** The refusal in a revoke's answer, or null when the answer is no CASH_POINT_IN_USE. */
export function cashPointRefusal(error: Answer | undefined): Refusal | null {
	if (error?.code !== 'CASH_POINT_IN_USE') return null;
	const listed = error.details?.tills;
	const names = Array.isArray(listed)
		? listed.filter((name): name is string => typeof name === 'string' && name !== '')
		: [];
	const first = error.details?.name;
	if (names.length === 0 && typeof first === 'string' && first !== '') names.push(first);
	return { names, text: error.message ?? '' };
}

/** The refusal in plain words: "Tablet 2 sends its cash to this till. ..." */
export function refusalWords(language: Language, refusal: Refusal): string {
	if (refusal.names.length === 0) return refusal.text;
	const names = refusal.names.join(isArabic(language) ? '، ' : ', ');
	return fill(
		message(
			language,
			refusal.names.length === 1 ? 'tillsCashPointInUse' : 'tillsCashPointInUseMany',
		),
		names,
	);
}

type TillBody = components['schemas']['TillBody'];

/** What a till's row says about its last heartbeat. */
export type TillReport =
	| { seen: false; text: string }
	| {
			seen: true;
			/** The online mark, from the server's own reading of its clock. */
			online: boolean;
			chip: string;
			/** "Last seen today 1:10 PM", then app build, waiting sales and platform when reported. */
			facts: string[];
	  };

/**
 * A till's report for its row. A till the server never heard, or an answer
 * from an API that names no report, says "Never seen" and nothing more. The
 * time is on the restaurant's clock (timeZone), never the browser's.
 */
export function tillReport(
	language: Language,
	till: Pick<TillBody, 'lastSeenAt' | 'online' | 'appBuild' | 'waitingSales' | 'platform'>,
	timeZone: string,
	now: Date = new Date(),
): TillReport {
	const when = formatWhen(
		till.lastSeenAt,
		language,
		{
			today: message(language, 'tillsSeenToday'),
			yesterday: message(language, 'tillsSeenYesterday'),
		},
		now,
		timeZone,
	);
	if (!when) return { seen: false, text: message(language, 'tillsNeverSeen') };
	const facts = [fill(message(language, 'tillsLastSeen'), when)];
	if (till.appBuild !== undefined)
		facts.push(fill(message(language, 'tillsAppBuild'), till.appBuild));
	if (till.waitingSales !== undefined) {
		const n = till.waitingSales;
		const key = n === 0 ? 'tillsWaitingSalesNone' : (`tillsWaitingSales${countForm(n)}` as const);
		facts.push(fill(message(language, key), n));
	}
	const platform = platformName(language, till.platform);
	if (platform) facts.push(platform);
	return {
		seen: true,
		online: till.online,
		chip: message(language, till.online ? 'tillsOnline' : 'tillsOffline'),
		facts,
	};
}

/** "Windows" or "Android" for the platform a heartbeat named (windows-x86_64, android-arm64); empty for another. */
export function platformName(language: Language, platform: string | undefined): string {
	if (platform?.startsWith('windows')) return message(language, 'tillsWindows');
	if (platform?.startsWith('android')) return message(language, 'tillsAndroid');
	return '';
}
